import { create } from 'zustand';

/**
 * High-frequency cursor state kept in a dedicated store so mouse-move updates do
 * not re-render the heavy browser/config panels. Distances are in metres along
 * the lap (0..lapLength).
 */
/**
 * What moved the shared cursor. `hover` means a genuine pointer hover over the
 * map, a graph, or the timeline; `playback` means the playback clock is scrubbing
 * the cursor to follow the reference car. The map uses this to only draw hover
 * markers for real hovers (playback has its own separate markers).
 */
export type CursorSource = 'hover' | 'playback';

interface CursorState {
  /** Live hover distance (metres), or null when the pointer is away. */
  active: number | null;
  /** What set `active`, or null when the cursor is cleared. */
  source: CursorSource | null;
  /** Locked distance from a click, for A–B differential; null when unlocked. */
  locked: number | null;
  setActive: (d: number | null, source?: CursorSource) => void;
  toggleLock: (d: number) => void;
  clearLock: () => void;
}

export const useCursorStore = create<CursorState>((set, get) => ({
  active: null,
  source: null,
  locked: null,
  setActive: (d, source = 'hover') => set({ active: d, source: d == null ? null : source }),
  toggleLock: (d) => {
    const current = get().locked;
    // Clicking near the existing lock clears it; otherwise (re)locks.
    set({ locked: current !== null && Math.abs(current - d) < 1 ? null : d });
  },
  clearLock: () => set({ locked: null }),
}));
